import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CandidateBulkTransferModal from "pages/AllCommonModal/CandidateBulkTransferModal/candidate-bulk-transfer-modal";
import { useTableHorizontalScrollEnd } from "hooks/useTableHorizontalScrollEnd";
import {
    Card,
    CardBody,
    Table,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledTooltip,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    citizenType,
    findTableColumnDefinitionByPreferenceKey,
    forClientAndJob,
    formatDateTimeValueForTable,
    formatMetadataTableCellDateTime,
    formatMetadataTableDateTimeForDisplay,
    isMetadataDateTimeTableColumnType,
    isApiDateTimeLikeValue,
    convertToDateAndTimeWithSeconds,
    phoneNumberShowFormat,
    getTableColumnPreferenceKey,
    idKey,
    incentiveType,
    onBoardingStatusType,
    quotaType,
    isCandidateTableYesNoColumn,
    getMetadataTableCellValue,
    isBooleanLikeMetadataTableValue,
    normalizeYesNoDisplayLabel,
    renderCandidateTableYesNoQuotaBadge,
    renderMetadataTableBooleanYesNoBadge,
    statusKey,
    statusType,
    statusTypeTrueFalse,
    DEFAULT_PAGE_SIZE,
    resolveCandidateTableKey,
} from "helpers/GeneralHelper";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
    saveHireStatus,
    fetchCommonHireStatusOptions,
    fetchHireStatusIncentiveStatusOptions,
} from "model/CandidateModel";
import {
    mapCommonDropdownRecords,
    HIRE_CLIENT_PAID_OPTIONS,
    hireStatusInlineKind,
    deriveHireDraftFromRow,
    hireUpdateSentForPayload,
} from "helpers/hireStatusInlineHelpers";
import { useTableColumnPreferences } from "libs/context/TableColumnPreferencesContext";
import { useTablePageSizePreference } from "libs/context/TablePageSizePreferencesContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import ManageColumnsModalFields from "components/Common/ManageColumnsModalFields";
import TableFilterToolbarActions from "components/Common/TableFilterToolbarActions";
import StatusMetadataBadge from "components/Common/StatusMetadataBadge";
import MaskedIdTableCell from "components/Common/MaskedIdTableCell";
import {
    isCandidateStatusMetadataAccessor,
    normalizeCandidateStatusMetadataValue,
} from "helpers/statusMetadataHelpers";
import RtrAcknowledgeElapsedTimer, {
    isRtrAcknowledgeAcknowledged,
    resolveRtrAcknowledgeCreatedDate,
} from "components/Common/RtrAcknowledgeElapsedTimer";
import {
    ALL_ROWS_PAGE_SIZE,
    buildPageSizeOptions,
    buildPaginationPageItems,
    DEFAULT_PAGE_SIZE_OPTIONS,
    formatPageSizeLabel,
    PAGINATION_ELLIPSIS,
    resolvePageSizeDisplayRange,
    scrollTablePaginationToTop,
} from "components/Common/ServerListPagination";

const getCandidateRowId = (item = {}) =>
  String(item?.id ?? item?._id ?? item?.candidateId ?? "").trim();

const PHONE_COLUMN_ACCESSORS = new Set([
    "phone",
    "phonenumber",
    "candidatephone",
    "candidate_phone",
]);

const isPhoneColumnAccessor = (accessor) =>
    PHONE_COLUMN_ACCESSORS.has(String(accessor ?? "").trim().toLowerCase());

const RTR_ACKNOWLEDGE_DATETIME_ACCESSORS = new Set([
    "createddate",
    "createdon",
    "createat",
    "acknowledgetime",
]);

const formatRtrAcknowledgeDateTimeForTable = (value, accessor) => {
    const normalized = String(accessor ?? "")
        .trim()
        .toLowerCase()
        .replace(/[_\s-]/g, "");
    if (RTR_ACKNOWLEDGE_DATETIME_ACCESSORS.has(normalized)) {
        return convertToDateAndTimeWithSeconds(value);
    }
    return formatDateTimeValueForTable(value, accessor);
};

const isMetadataLabelTableColumn = (columnDefinition = {}) =>
    String(columnDefinition?.type ?? "").trim().toLowerCase() === "label";

const formatRtrAcknowledgeMetadataLabelForTable = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return "—";
    }
    return String(value);
};

const renderRtrAcknowledgeMetadataCellValue = (cellValue, columnDefinition = {}) => {
    if (isMetadataLabelTableColumn(columnDefinition)) {
        return formatRtrAcknowledgeMetadataLabelForTable(cellValue);
    }
    return formatRtrAcknowledgeDateTimeForTable(cellValue, columnDefinition?.accessor);
};

const hasTruthyRtrAcknowledgeField = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

const isRtrAcknowledgePending = (item = {}) =>
    !hasTruthyRtrAcknowledgeField(item?.decisionDisplay ?? item?.decision_display) &&
    !hasTruthyRtrAcknowledgeField(item?.acknowledgeType ?? item?.acknowledge_type);

const isBadFitTrackingUnresolved = (item = {}) => {
    const resolved =
        item?.isResolved ??
        item?._originalBadFit?.isResolved ??
        item?._originalCandidate?.isResolved;
    if (resolved === true || String(resolved).trim().toLowerCase() === "true") {
        return false;
    }
    if (resolved === false || String(resolved).trim().toLowerCase() === "false") {
        return true;
    }
    return false;
};

const getBadFitTrackingIdFromRow = (item = {}) =>
    String(
        item?.aiCandidateBadFitTrackingId ??
            item?.aICandidateBadFitTrackingId ??
            item?._originalBadFit?.aiCandidateBadFitTrackingId ??
            item?._originalBadFit?.aICandidateBadFitTrackingId ??
            item?.id ??
            ""
    ).trim();

const getCandidateColumnWidthClass = (accessor, { wideDateAccessors, apiKeyAccessors, phoneAccessors }, pageName = "") => {
    const normalizedAccessor = String(accessor ?? "").trim().toLowerCase();
    const isBadFitPage = String(pageName ?? "").trim().toLowerCase() === "badfitresolution";

    if (isBadFitPage) {
        if (normalizedAccessor === "originalreasontext") {
            return "candidate-col-width-bad-fit-reason";
        }
        if (normalizedAccessor === "candidatename") {
            return "candidate-col-width-bad-fit-candidate";
        }
        if (normalizedAccessor === "clientname") {
            return "candidate-col-width-bad-fit-client";
        }
    }

    if (normalizedAccessor === "originalreasontext") {
        return "candidate-col-width-original-reason";
    }
    if (wideDateAccessors.has(accessor)) return "candidate-col-width-date";
    if (apiKeyAccessors.has(accessor)) return "candidate-col-width-api-key";
    if (phoneAccessors.has(String(accessor ?? "").trim().toLowerCase())) {
        return "candidate-col-width-phone";
    }
    return "";
};

/** Match column accessors for name / email styling (Candidate table). */
const isCandidateNameOrEmailColumnAccessor = (accessor) => {
  const item = String(accessor ?? "").trim().toLowerCase();
  return [
    "name",
    "email",
    "fullname",
    "client",
    "candidatefullname",
    "candidatedisplayname",
    "recruiterdisplayname",
  ].includes(item);
};

/** Client / company column accessors — Candidate table */
const isCandidateTableClientCompanyColumnAccessor = (accessor) => {
  const item = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  return [
    "client",
    "clientname",
    "clientdisplayname",
    "company",
    "companyname",
    "closebyusername",
  ].includes(item);
};

const mergeCandidateTableDataCellClasses = (accessor) =>
  [
    isCandidateNameOrEmailColumnAccessor(accessor) && "common-candidate-table-name-email-cell",
    isCandidateTableClientCompanyColumnAccessor(accessor) && "common-candidate-table-client-company-cell",
  ]
    .filter(Boolean)
    .join(" ") || undefined;

const TABLE_STATUS_BADGE_CLASS = "px-2 py-1 fw-semibold common-table-status-badge";

const STATUS_STYLE_ACCESSORS = [
    "status",
    "lastStatus",
    "apiStatus",
    "profileStatus",
    "orderStatus",
    "mecStatus",
    "drugStatus",
    "updateSend",
    "fadvSend",
    "isFadvSent",
    "funnelStatus",
    "funnlStatus",
];

const isCandidateTableStatusMetadataColumn = (accessor, columnType) => {
    if (isCandidateStatusMetadataAccessor(accessor)) return true;
    if (STATUS_STYLE_ACCESSORS.includes(accessor)) return true;
    const normalized = String(accessor ?? "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "");
    return (
        normalized === "profilestatus" ||
        normalized === "orderstatus" ||
        normalized === "mecstatus" ||
        normalized === "drugstatus" ||
        normalized === "updatesend" ||
        normalized === "fadvsend" ||
        normalized === "isfadvsent" ||
        normalized === "funnelstatus" ||
        normalized === "funnlstatus"
    );
};

const isWeeklyReviewRagStatusAccessor = (accessor) => {
  const normalized = String(accessor ?? "").replace(/_/g, "").toLowerCase();
  return (
    normalized === "status" ||
    normalized === "statusthisweek" ||
    normalized === "statuslastweek" ||
    normalized === "thisweekstatus" ||
    normalized === "lastweekstatus"
  );
};

const isStatusCandidateTableColumnAccessor = (accessor) => {
  if (isCandidateTableStatusMetadataColumn(accessor)) return true;
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  if (
    normalized === "status" ||
    normalized === "laststatus" ||
    normalized === "apistatus" ||
    normalized === "profilestatus" ||
    normalized === "orderstatus" ||
    normalized === "mecstatus" ||
    normalized === "drugstatus"
  ) {
    return true;
  }
  if (statusKey(accessor) === "status") return true;
  return isWeeklyReviewRagStatusAccessor(accessor);
};

const isIdCandidateTableColumnAccessor = (accessor) =>
  String(accessor ?? "").trim().toLowerCase().replace(/_/g, "") === "id" ||
  idKey(accessor) === "id";

/** Wider columns for long pipeline status labels (All Candidates). */
const CANDIDATE_PIPELINE_STATUS_ACCESSORS = new Set([
  "profilestatus",
  "orderstatus",
  "mecstatus",
  "drugstatus",
]);

const isCandidateFunnlStatusColumn = (accessor) => {
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return normalized === "funnlstatus" || normalized === "funnelstatus";
};

const isCandidatePipelineStatusColumn = (accessor) =>
  CANDIDATE_PIPELINE_STATUS_ACCESSORS.has(
    String(accessor ?? "").trim().toLowerCase().replace(/_/g, "")
  );

const getCandidateTableStatusColClass = (accessor, pageName) => {
  if (!isStatusCandidateTableColumnAccessor(accessor)) return "";
  const normalizedPage = String(pageName || "").toLowerCase();
  const normalizedAccessor = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  if (isCandidateFunnlStatusColumn(accessor)) {
    return "common-table-candidate-funnl-status-col";
  }
  if (isCandidatePipelineStatusColumn(accessor)) {
    return "common-table-candidate-pipeline-status-col";
  }
  if (normalizedPage === "fadv-csv-log" && normalizedAccessor === "status") {
    return "common-table-status-col--wide";
  }
  return "common-table-status-col";
};

const isFadvCsvLogClientColumn = (accessor, pageName) =>
  String(pageName || "").toLowerCase() === "fadv-csv-log" &&
  isCandidateTableClientCompanyColumnAccessor(accessor);

const isAllCandidatesClientColumn = (accessor, pageName) =>
  String(pageName || "").toLowerCase() === "candidates" &&
  isCandidateTableClientCompanyColumnAccessor(accessor);

const isRtrAcknowledgeClientColumn = (accessor, pageName) =>
  String(pageName || "").toLowerCase() === "rtracknowledge" &&
  isCandidateTableClientCompanyColumnAccessor(accessor);

const RTR_ACKNOWLEDGE_CREATED_DATE_ACCESSOR = "createddate";

const isRtrAcknowledgeCreatedDateColumn = (accessor, pageName) => {
  if (String(pageName || "").toLowerCase() !== "rtracknowledge") return false;
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, "");
  return normalized === RTR_ACKNOWLEDGE_CREATED_DATE_ACCESSOR;
};

const isRtrAcknowledgeCreatedByDisplayColumn = (accessor, pageName) => {
  if (String(pageName || "").toLowerCase() !== "rtracknowledge") return false;
  const normalized = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]/g, "");
  return normalized === "createdbydisplay";
};

const getCandidateTableClientColClass = (accessor, pageName) => {
  if (isFadvCsvLogClientColumn(accessor, pageName)) {
    return "common-table-client-col--wide";
  }
  if (isAllCandidatesClientColumn(accessor, pageName)) {
    return "common-table-client-col--all-candidates";
  }
  if (isRtrAcknowledgeClientColumn(accessor, pageName)) {
    return "common-table-client-col--rtr-acknowledge";
  }
  return "";
};

const getCandidateTableCreatedColClass = (accessor, pageName) => {
  if (isRtrAcknowledgeCreatedDateColumn(accessor, pageName)) {
    return "common-table-created-col--rtr-acknowledge";
  }
  return "";
};

const getCandidateTableCreatedByDisplayColClass = (accessor, pageName) => {
  if (isRtrAcknowledgeCreatedByDisplayColumn(accessor, pageName)) {
    return "common-table-created-by-col--rtr-acknowledge";
  }
  return "";
};

/** Processed / download columns — center-aligned. */
const isCenterAlignedCandidateTableColumnAccessor = (accessor, columnType) => {
  if (isCandidateTableYesNoColumn(accessor, columnType)) return true;
  if (isStatusCandidateTableColumnAccessor(accessor)) return true;
  const normalized = String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");
  return (
    normalized === "dataprocessed" ||
    normalized === "processed" ||
    normalized === "download" ||
    normalized === "downloadurl" ||
    normalized === "link" ||
    normalized === "file" ||
    normalized === "message"
  );
};

const getCandidateTableColumnHeaderClassName = (accessor, widthContext, pageName, columnType) => {
  const classNames = [];
  const normalizedAccessor = String(accessor ?? "").trim().toLowerCase();
  const shouldAllowColumnTextWrap =
    normalizedAccessor === "originalreasontext" ||
    (String(pageName ?? "").trim().toLowerCase() === "badfitresolution" &&
      ["candidatename", "clientname"].includes(normalizedAccessor));

  if (
    !isCandidateFunnlStatusColumn(accessor) &&
    !isFadvCsvLogClientColumn(accessor, pageName) &&
    !isAllCandidatesClientColumn(accessor, pageName) &&
    !isRtrAcknowledgeClientColumn(accessor, pageName) &&
    !isRtrAcknowledgeCreatedByDisplayColumn(accessor, pageName) &&
    !shouldAllowColumnTextWrap
  ) {
    classNames.push("text-nowrap");
  }
  if (isCenterAlignedCandidateTableColumnAccessor(accessor, columnType)) {
    classNames.push("text-center");
  } else {
    classNames.push("text-start");
  }
  if (isIdCandidateTableColumnAccessor(accessor)) {
    classNames.push("common-table-id-col");
  }
  const statusColClass = getCandidateTableStatusColClass(accessor, pageName);
  if (statusColClass) classNames.push(statusColClass);
  const clientColClass = getCandidateTableClientColClass(accessor, pageName);
  if (clientColClass) classNames.push(clientColClass);
  const createdColClass = getCandidateTableCreatedColClass(accessor, pageName);
  if (createdColClass) classNames.push(createdColClass);
  const createdByDisplayColClass = getCandidateTableCreatedByDisplayColClass(accessor, pageName);
  if (createdByDisplayColClass) classNames.push(createdByDisplayColClass);
  const widthClass = getCandidateColumnWidthClass(accessor, widthContext, pageName);
  if (widthClass) classNames.push(widthClass);
  return classNames.join(" ");
};

const getCandidateTableColumnCellClassName = (accessor, pageName, columnType) => {
  const classNames = ["align-middle"];
  if (isCenterAlignedCandidateTableColumnAccessor(accessor, columnType)) {
    classNames.push("text-center");
  } else {
    classNames.push("text-start");
  }
  if (isIdCandidateTableColumnAccessor(accessor)) {
    classNames.push("common-table-id-col");
  }
  const statusColClass = getCandidateTableStatusColClass(accessor, pageName);
  if (statusColClass) classNames.push(statusColClass);
  const clientColClass = getCandidateTableClientColClass(accessor, pageName);
  if (clientColClass) classNames.push(clientColClass);
  const createdColClass = getCandidateTableCreatedColClass(accessor, pageName);
  if (createdColClass) classNames.push(createdColClass);
  const createdByDisplayColClass = getCandidateTableCreatedByDisplayColClass(accessor, pageName);
  if (createdByDisplayColClass) classNames.push(createdByDisplayColClass);
  const dataCellClassName = mergeCandidateTableDataCellClasses(accessor);
  if (dataCellClassName) classNames.push(dataCellClassName);
  return classNames.join(" ");
};

const CommonTableForCandidate = ({ columns,
    data,
    isActionRequired,
    onEdit,
    onDelete,
    onDetails,
    /** When set, Call details renders as a router link (supports right-click → open in new tab). */
    getDetailsHref,
    onToggleAllowed,
    onView,
    isCheckboxLeftSide,
    isPagination,
    pageName,
    tableKey: tableKeyProp,
    currentPage: controlledCurrentPage,
    rowsPerPage: controlledRowsPerPage,
    totalRecords: controlledTotalRecords,
    totalPages: controlledTotalPages,
    onPageChange,
    onPageSizeChange,
    setCommonFilterModalOpen,
    setCommonResetFilter,
    allCommonFilter,
    activeFilterCount = 0,
    onBill,
    onPermalink,
    onOffboarding,
    onIncentiveTarget,
    onJobDescription,
    onAppreciation,
    onPreQuestioning,
    onAdvantageLogin,
    onVerifyTestApprove,
    onVerifyTestCancel,
    onBadFitConvertToGoodFit,
    onRtrAcknowledgeYes,
    onRtrAcknowledgeNo,
    onRtrAcknowledgeCall,
    onRowNotesChange,
    onRowNotesBlur,
    onAddClosure,
    onMarkAsHired,
    onMarkAsRejected,
    onTodayCall,
    onSureMailSend,
    onResetCandidate,
    onViewImage,
    onTriggerFadv,
    onDriverDetails,
    enableBulkDeactivate,
    onDeactivateCandidates,
    enableBulkTransfer = false,
    onBulkTransferCandidates,
    enableMoveToAiRecruiterSchedule = false,
    onMoveToAiRecruiterSchedule,
    onHireStatusSaved,
    /** Prefer this for hire-status row Edit; falls back to `onEdit`. Opens hire-status add/edit modal from the page. */
    onHireStatusEdit,
    /** Optional per-row class name (e.g. deactive candidate highlight). */
    getRowClassName,
    /** Custom empty tbody content (e.g. filter prompt). Falls back to "No data found". */
    emptyTableContent,
}) => {
    const resolvedTableKey = useMemo(
        () => resolveCandidateTableKey(pageName, tableKeyProp),
        [tableKeyProp, pageName]
    );

    const { scrollRef: tableScrollRef, isScrolledToEnd } = useTableHorizontalScrollEnd();

    const {
        columnOrder,
        visibleColumnOrder,
        orderedColumns,
        visibleColumns,
        handleColumnDragEnd,
        handleColumnToggle,
    } = useTableColumnPreferences(resolvedTableKey, columns);
    const isServerPagination = typeof onPageChange === "function";
    const defaultClientPageSize = Number(controlledRowsPerPage) || 5;
    const { pageSize: preferredPageSize, setPageSize: persistPageSize, settingsReady } =
        useTablePageSizePreference(
            resolvedTableKey,
            isServerPagination ? Number(controlledRowsPerPage) || DEFAULT_PAGE_SIZE : defaultClientPageSize
        );
    const { permissionData } = usePermissionContext() || {};
    const canShowAllRows = Boolean(permissionData?.CandidateShowAllAccess);
    const pageSizeOptions = useMemo(
        () => buildPageSizeOptions(DEFAULT_PAGE_SIZE_OPTIONS, canShowAllRows),
        [canShowAllRows]
    );

    const [rowsPerPage, setRowsPerPage] = useState(() =>
        isServerPagination
            ? Number(controlledRowsPerPage) || preferredPageSize
            : preferredPageSize
    );
    const [currentPage, setCurrentPage] = useState(controlledCurrentPage || 1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
    const hasAppliedPersistedPageSizeRef = useRef(false);
    const normalizedPageName = String(pageName || "").toLowerCase();
    const isCandidateHireStatusPage = normalizedPageName === "candidate-hire-status";
    const isCallBackRequestPage = normalizedPageName === "call-back-request";
    const isVerifyTestPage =
        normalizedPageName === "verify-test" || normalizedPageName === "verifytest";
    const isCallAuditPage = normalizedPageName === "callaudit";
    const isFadvCsvLogPage = normalizedPageName === "fadv-csv-log";
    const isFadvOrderLogPage = normalizedPageName === "fadv-order-log";
    const isFadvProfileOrderLogPage = normalizedPageName === "fadv-profile-order-log";
    const isModifyClosurePage = normalizedPageName === "modifyclosure";
    const isRtrAcknowledgePage = normalizedPageName === "rtracknowledge";
    const isBadFitResolutionPage = normalizedPageName === "badfitresolution";
    const showRtrAcknowledgeTimerColumn = isRtrAcknowledgePage && isActionRequired;
    const showRtrAcknowledgeCallColumn =
        isRtrAcknowledgePage &&
        isActionRequired &&
        typeof onRtrAcknowledgeCall === "function";
    const isCompactCandidateActionColumn =
        isCandidateHireStatusPage ||
        isVerifyTestPage ||
        isCallAuditPage ||
        isCallBackRequestPage ||
        isFadvCsvLogPage ||
        isFadvOrderLogPage ||
        isFadvProfileOrderLogPage ||
        isModifyClosurePage ||
        isBadFitResolutionPage;

    const [hireInlineEdits, setHireInlineEdits] = useState({});
    const [hireOrderStatusOptions, setHireOrderStatusOptions] = useState([]);
    const [hireIncentiveTypeOptions, setHireIncentiveTypeOptions] = useState([]);
    const [hireStatusSavingId, setHireStatusSavingId] = useState(null);

    useEffect(() => {
        if (!isCandidateHireStatusPage) return;
        let cancelled = false;
        (async () => {
            try {
                const [hireStatusRes, incRes] = await Promise.all([
                    fetchCommonHireStatusOptions(),
                    fetchHireStatusIncentiveStatusOptions(),
                ]);
                if (cancelled) return;
                setHireOrderStatusOptions(mapCommonDropdownRecords(hireStatusRes?.data));
                setHireIncentiveTypeOptions(mapCommonDropdownRecords(incRes?.data));
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.message || "Unable to load hire status options");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isCandidateHireStatusPage]);

    useEffect(() => {
        if (!isCandidateHireStatusPage) return;
        setHireInlineEdits({});
    }, [isCandidateHireStatusPage, controlledCurrentPage, controlledRowsPerPage]);

    const mergeHireDraft = useCallback(
        (item) => {
            const id = String(item?.id ?? item?._id ?? "").trim();
            return {
                ...deriveHireDraftFromRow(item),
                ...(hireInlineEdits[id] || {}),
            };
        },
        [hireInlineEdits]
    );

    /** Local-only draft update (e.g. comments while typing — saved on blur). */
    const setHireDraftLocal = useCallback((item, partial) => {
        const id = String(item?.id ?? item?._id ?? "").trim();
        if (!id) return;
        setHireInlineEdits((prev) => {
            const base = { ...deriveHireDraftFromRow(item), ...(prev[id] || {}) };
            return { ...prev, [id]: { ...base, ...partial } };
        });
    }, []);

    /**
     * Merge partial into row draft and POST `HireStatus` (same payload as hire-status modal).
     * Used on inline control change — no separate save button.
     */
    const persistHireStatusAfterChange = useCallback(
        async (item, partial = {}) => {
            const id = String(item?.id ?? item?._id ?? "").trim();
            if (!id) {
                toast.error("Record ID not found");
                return;
            }
            const raw = item?._originalHireStatus ?? item;
            let merged;
            setHireInlineEdits((prev) => {
                merged = {
                    ...deriveHireDraftFromRow(item),
                    ...(prev[id] || {}),
                    ...partial,
                };
                return { ...prev, [id]: merged };
            });
            setHireStatusSavingId(id);
            try {
                const dateHiredIso = merged?.dateHired
                    ? dayjs(merged.dateHired).startOf("day").toISOString()
                    : null;
                const payload = {
                    id,
                    dateHired: dateHiredIso,
                    incentiveStatus: merged.incentiveStatus || null,
                    clientPaid: merged.clientPaid || null,
                    status: merged.status || null,
                    comments: merged.comments?.trim() ? merged.comments.trim() : null,
                    updateSent: hireUpdateSentForPayload(raw),
                };
                const response = await saveHireStatus(payload);
                if (response?.success || response?.isSuccess || response?.status) {
                    setHireInlineEdits((prev) => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                    });
                    onHireStatusSaved?.({
                        id,
                        dateHired: dateHiredIso,
                        incentiveStatus: merged.incentiveStatus || null,
                        clientPaid: merged.clientPaid || null,
                        status: merged.status || null,
                        orderStatus: merged.status || null,
                        comments: merged.comments?.trim() ? merged.comments.trim() : null,
                    });
                } else {
                    toast.error(response?.message || response?.error?.message || "Unable to save");
                }
            } catch (e) {
                toast.error(e?.message || "Unable to save");
            } finally {
                setHireStatusSavingId(null);
            }
        },
        [onHireStatusSaved]
    );

    useEffect(() => {
        if (controlledCurrentPage) {
            setCurrentPage(controlledCurrentPage);
        }
    }, [controlledCurrentPage]);

    useEffect(() => {
        if (Number.isFinite(Number(controlledRowsPerPage))) {
            setRowsPerPage(Number(controlledRowsPerPage));
        }
    }, [controlledRowsPerPage]);

    useEffect(() => {
        if (!isServerPagination || !settingsReady || hasAppliedPersistedPageSizeRef.current) return;

        const controlledSize = Number(controlledRowsPerPage);
        let nextPreferred = Number(preferredPageSize);

        if (nextPreferred === ALL_ROWS_PAGE_SIZE && !canShowAllRows) {
            hasAppliedPersistedPageSizeRef.current = true;
            return;
        }

        // Preference All + API/parent already returned a positive size → accept it, stop refetch loop.
        if (
            nextPreferred === ALL_ROWS_PAGE_SIZE &&
            Number.isFinite(controlledSize) &&
            controlledSize > 0
        ) {
            hasAppliedPersistedPageSizeRef.current = true;
            if (controlledSize !== nextPreferred) {
                persistPageSize(controlledSize);
            }
            return;
        }

        hasAppliedPersistedPageSizeRef.current = true;
        if (
            Number.isFinite(nextPreferred) &&
            nextPreferred !== controlledSize &&
            typeof onPageSizeChange === "function"
        ) {
            onPageSizeChange(nextPreferred);
        }
    }, [
        isServerPagination,
        settingsReady,
        preferredPageSize,
        controlledRowsPerPage,
        onPageSizeChange,
        canShowAllRows,
        persistPageSize,
    ]);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [visibleApiKeyMap, setVisibleApiKeyMap] = useState({});
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkTransferModalOpen, setBulkTransferModalOpen] = useState(false);
    const [moveToAiRecruiterScheduleLoading, setMoveToAiRecruiterScheduleLoading] =
        useState(false);
    const headerCheckboxRef = useRef(null);
    const enableBulkSelection = Boolean(
        enableBulkDeactivate || enableBulkTransfer || enableMoveToAiRecruiterSchedule
    );

    const handleSort = (accessor) => {
        setSortConfig((prev) => {
            if (prev.key === accessor) {
                return {
                    key: accessor,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key: accessor, direction: "asc" };
        });
    };

    const toggleApiKeyVisibility = (rowKey, accessor) => {
        const visibilityKey = `${rowKey}-${accessor}`;
        setVisibleApiKeyMap((prev) => ({
            ...prev,
            [visibilityKey]: !prev?.[visibilityKey],
        }));
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return data;
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            return sortConfig.direction === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }, [data, sortConfig]);

    const isAllRowsPageSize = Number(rowsPerPage) === ALL_ROWS_PAGE_SIZE;
    const totalPages = isServerPagination
        ? controlledTotalPages || 1
        : isAllRowsPageSize
            ? 1
            : Math.ceil(sortedData.length / rowsPerPage) || 1;
    const startIndex = isAllRowsPageSize ? 0 : (currentPage - 1) * rowsPerPage;
    const paginatedData = isPagination
        ? isServerPagination || isAllRowsPageSize
            ? sortedData
            : sortedData.slice(startIndex, startIndex + rowsPerPage)
        : sortedData;

    const pageRowIds = useMemo(
        () => (paginatedData || []).map((item) => getCandidateRowId(item)).filter(Boolean),
        [paginatedData]
    );

    const allPageSelected =
        enableBulkSelection &&
        pageRowIds.length > 0 &&
        pageRowIds.every((id) => selectedIds.has(id));
    const somePageSelected =
        enableBulkSelection && pageRowIds.some((id) => selectedIds.has(id));

    const totalRecordsCount = isServerPagination
        ? controlledTotalRecords ?? data?.length ?? 0
        : data?.length ?? 0;
    const { displayFrom, displayTo } = resolvePageSizeDisplayRange(
        currentPage,
        rowsPerPage,
        totalRecordsCount
    );

    useEffect(() => {
        if (!enableBulkSelection) return;
        setSelectedIds(new Set());
    }, [enableBulkSelection, controlledCurrentPage, controlledRowsPerPage]);

    useEffect(() => {
        if (!headerCheckboxRef.current || !enableBulkSelection) return;
        headerCheckboxRef.current.indeterminate = somePageSelected && !allPageSelected;
    }, [somePageSelected, allPageSelected, enableBulkSelection]);

    const toggleRowId = (id) => {
        if (!id) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAllOnPage = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const allSel = pageRowIds.length > 0 && pageRowIds.every((rid) => next.has(rid));
            if (allSel) {
                pageRowIds.forEach((rid) => next.delete(rid));
            } else {
                pageRowIds.forEach((rid) => next.add(rid));
            }
            return next;
        });
    };

    const handleBulkDeactivateClick = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length || !onDeactivateCandidates) return;
        const result = await Swal.fire({
            title: "Warning",
            text: "Are you sure you want to deactivate the selected candidates?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;
        await onDeactivateCandidates(ids);
        setSelectedIds(new Set());
    };

    const selectedCandidatesForBulkTransfer = useMemo(
        () =>
            (Array.isArray(data) ? data : [])
                .filter((item) => selectedIds.has(getCandidateRowId(item)))
                .map((item) => {
                    const raw = item?._originalCandidate ?? item;
                    const name =
                        String(
                            item?.name ??
                                `${raw?.firstName ?? ""} ${raw?.lastName ?? ""}`.trim() ??
                                raw?.name ??
                                ""
                        ).trim() || getCandidateRowId(item);
                    return {
                        id: getCandidateRowId(item),
                        name,
                    };
                }),
        [data, selectedIds]
    );

    const handleBulkTransferClick = () => {
        if (!selectedIds.size || !onBulkTransferCandidates) return;
        setBulkTransferModalOpen(true);
    };

    const handleBulkTransferSubmit = async (payload = {}) => {
        if (!onBulkTransferCandidates) return false;
        const didTransfer = await onBulkTransferCandidates(payload);
        if (didTransfer) {
            setSelectedIds(new Set());
        }
        return didTransfer;
    };

    const closeBulkTransferModal = () => {
        setBulkTransferModalOpen(false);
    };

    const handleMoveToAiRecruiterScheduleClick = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length || !onMoveToAiRecruiterSchedule || moveToAiRecruiterScheduleLoading) {
            return;
        }
        const result = await Swal.fire({
            title: "Move to AI Recruiter Schedule?",
            text: "Are you sure you want to move the selected candidates to AI recruiter schedule?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, move",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return;

        setMoveToAiRecruiterScheduleLoading(true);
        try {
            const didMove = await onMoveToAiRecruiterSchedule(ids);
            if (didMove) {
                setSelectedIds(new Set());
            }
        } finally {
            setMoveToAiRecruiterScheduleLoading(false);
        }
    };

    const handleRowsPerPageChange = (num) => {
        persistPageSize(num);
        if (isServerPagination) {
            onPageSizeChange?.(num);
            return;
        }
        setRowsPerPage(num);
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage <= 1) return;
        const nextPage = currentPage - 1;
        scrollTablePaginationToTop();
        if (isServerPagination) {
            onPageChange?.(nextPage);
            return;
        }
        setCurrentPage(nextPage);
    };

    const handleNextPage = () => {
        if (currentPage >= totalPages) return;
        const nextPage = currentPage + 1;
        scrollTablePaginationToTop();
        if (isServerPagination) {
            onPageChange?.(nextPage);
            return;
        }
        setCurrentPage(nextPage);
    };

    const handlePageNumberClick = (page) => {
        if (!page || page === currentPage) return;
        scrollTablePaginationToTop();
        if (isServerPagination) {
            onPageChange?.(page);
            return;
        }
        setCurrentPage(page);
    };

    const paginationPageItems = useMemo(
        () => buildPaginationPageItems(currentPage, totalPages),
        [currentPage, totalPages]
    );

    // SVG Icons
    const IconColumns = (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 3h18v2H3V3zm0 8h18v2H3v-2zm0 8h18v2H3v-2z" />
        </svg>
    );
    const IconFilter = (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 18h4v-2h-4v2zm-7-8v2h18v-2H3zm3-6v2h12V4H6z" />
        </svg>
    );
    const IconList = (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h2v-2H3v2zm0 6h2v-2H3v2zm0-12h2V5H3v2zm4 0h14V5H7v2zm0 4h14v-2H7v2zm0 4h14v-2H7v2zm0 4h14v-2H7v2z" />
        </svg>
    );
    const IconDownload = (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M5 20h14v-2H5v2zM12 2v12l4-4h-3V2h-2v8H8l4 4z" />
        </svg>
    );
    const IconEdit = (
        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.99-1.66z" />
        </svg>
    );

    const IconDetails = (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 640 640"><path d="M320 128C241 128 175.3 185.3 162.3 260.7C171.6 257.7 181.6 256 192 256L208 256C234.5 256 256 277.5 256 304L256 400C256 426.5 234.5 448 208 448L192 448C139 448 96 405 96 352L96 288C96 164.3 196.3 64 320 64C443.7 64 544 164.3 544 288L544 456.1C544 522.4 490.2 576.1 423.9 576.1L336 576L304 576C277.5 576 256 554.5 256 528C256 501.5 277.5 480 304 480L336 480C362.5 480 384 501.5 384 528L384 528L424 528C463.8 528 496 495.8 496 456L496 435.1C481.9 443.3 465.5 447.9 448 447.9L432 447.9C405.5 447.9 384 426.4 384 399.9L384 303.9C384 277.4 405.5 255.9 432 255.9L448 255.9C458.4 255.9 468.3 257.5 477.7 260.6C464.7 185.3 399.1 127.9 320 127.9z" /></svg>
    );

    const IconView = (
        <svg xmlns="www.w3.org" width="14" height="14" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0" />
        </svg>

    );

    const SortIcon = ({ column }) => {
        const isActive = sortConfig.key === column;
        const direction = isActive ? sortConfig.direction : null;
        const activeColor = "#212529";
        const inactiveColor = "#c4c4c4";
        return (
            <span
                style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    lineHeight: 0,
                    marginLeft: 4,
                    verticalAlign: "middle",
                }}
            >
                <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block" }}>
                    <path d="M4 0l4 5H0z" fill={direction === "asc" ? activeColor : inactiveColor} />
                </svg>
                <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: "block", marginTop: 2 }}>
                    <path d="M4 5L0 0h8z" fill={direction === "desc" ? activeColor : inactiveColor} />
                </svg>
            </span>
        );
    };

    // Column modal
    const [showColumnModal, setShowColumnModal] = useState(false);
    const toggleColumnModal = () => setShowColumnModal(!showColumnModal);

    const wideDateAccessors = new Set(["billDate", "regDate", "registerDate"]);
    const apiKeyAccessors = new Set(["apiKey", "api_key"]);
    const phoneAccessors = new Set([...PHONE_COLUMN_ACCESSORS]);

    // Drag style function
    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        cursor: "grab",
        background: isDragging ? "#f1f5f9" : "transparent",
        boxShadow: isDragging ? "0 4px 8px rgba(0,0,0,0.15)" : "none",
        transition: "all 0.2s ease",
        ...draggableStyle,
    });

    const toSnakeCase = (value = "") =>
        String(value)
            .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
            .replace(/[\s-]+/g, "_")
            .toLowerCase();

    const toCamelCase = (value = "") =>
        String(value)
            .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
            .replace(/^(.)/, (m) => m.toLowerCase());

    const toPascalCase = (value = "") => {
        const camel = toCamelCase(value);
        return camel ? camel.charAt(0).toUpperCase() + camel.slice(1) : "";
    };

    const getItemValueByAccessor = (item, accessor) => {
        if (!item || !accessor) return "";
        if (item?.[accessor] !== undefined && item?.[accessor] !== null) return item[accessor];

        const normalizedAccessor = String(accessor).toLowerCase();
        const candidates = [
            accessor,
            toCamelCase(accessor),
            toPascalCase(accessor),
            toSnakeCase(accessor),
            String(accessor).toLowerCase(),
            String(accessor).toUpperCase(),
        ];

        for (const key of candidates) {
            if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
        }

        const matchedKey = Object.keys(item).find(
            (key) => String(key).toLowerCase() === normalizedAccessor
        );
        if (matchedKey && item?.[matchedKey] !== undefined && item?.[matchedKey] !== null) {
            return item[matchedKey];
        }
        return "";
    };

    const resolveTableCellValue = (item, accessor) => {
        const metadataValue = getMetadataTableCellValue(item, accessor);
        if (metadataValue !== undefined) return metadataValue;
        return getItemValueByAccessor(item, accessor);
    };

    const resolveColumnValue = (item, accessor, columnType) => {
        const value = resolveTableCellValue(item, accessor);

        switch (accessor) {
            case "status":
                return value ?? "";

            case "incentive":
                return value ?? "";

            case "video":
            case "vLink":
                return value || "";

            case "notes":
            case "fedex_id":
                return value || "";

            case "candidateAndSource":
            case "clientAndRecruiter":
                return value ?? "";

            case "last_activation_date":
            case "lastActivationDate":
                return formatDateTimeValueForTable(value, accessor);

            default: {
                if (isBooleanLikeMetadataTableValue(value)) {
                    return normalizeYesNoDisplayLabel(value) ?? "";
                }
                if (isMetadataDateTimeTableColumnType(columnType) || isApiDateTimeLikeValue(value)) {
                    return formatMetadataTableDateTimeForDisplay(value) || "";
                }
                if (isMetadataLabelTableColumn({ type: columnType })) {
                    return value ?? "";
                }
                if (isCandidateTableStatusMetadataColumn(accessor, columnType)) {
                    const normalized = normalizeCandidateStatusMetadataValue(accessor, value);
                    if (normalized === null || normalized === undefined || normalized === "") {
                        return "";
                    }
                    return String(normalized);
                }
                if (isCandidateTableYesNoColumn(accessor, columnType)) {
                    return normalizeYesNoDisplayLabel(value) ?? "";
                }
                return formatMetadataTableCellDateTime(value, columnType, accessor);
            }
        }
    };



    const getVisibleColumns = () =>
        visibleColumnOrder
            .map((preferenceKey) =>
                findTableColumnDefinitionByPreferenceKey(columns, preferenceKey)
            )
            .filter(Boolean);

    const visibleColumnCount = getVisibleColumns()?.length || 0;
    const noDataColSpan =
        visibleColumnCount +
        (isCheckboxLeftSide ? 1 : 0) +
        (showRtrAcknowledgeTimerColumn ? 1 : 0) +
        (showRtrAcknowledgeCallColumn ? 1 : 0) +
        (isActionRequired ? 1 : 0);
    const showFixedFilterPrompt = !paginatedData?.length && Boolean(emptyTableContent);

    // const getTableData = () =>
    //   sortedData.map((row) =>
    //     getVisibleColumns().map((col) => row[col.accessor] ?? "")
    //   );

    const getTableData = () =>
        sortedData.map((row) =>
            getVisibleColumns().map((col) =>
                resolveColumnValue(row, col.accessor, col?.type)
            )
        );


    const handleCopy = async () => {
        const headers = getVisibleColumns().map((c) => c.Header).join("\t");
        const rows = getTableData()
            .map((r) => r.join("\t"))
            .join("\n");

        await navigator.clipboard.writeText(`${headers}\n${rows}`);
    };
    const handleCSV = () => {
        const headers = getVisibleColumns().map((c) => c.Header);
        const rows = getTableData();

        const csvContent = [headers, ...rows]
            .map((e) => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${pageName}  - (DEV) Ancile Hiring Tools.csv`;
        link.click();
    };
    // const handleExcel = () => {
    //   const wsData = [
    //     getVisibleColumns().map((c) => c.Header),
    //     ...getTableData(),
    //   ];

    //   const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    //   const workbook = XLSX.utils.book_new();
    //   XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    //   XLSX.writeFile(workbook, "table.xlsx");
    // };

    const handleExcel = () => {
        const headers = getVisibleColumns().map(col => col.Header);
        const rows = getTableData();

        const worksheetData = [headers, ...rows];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        const range = XLSX.utils.decode_range(worksheet["!ref"]);

        for (let r = range.s.r; r <= range.e.r; r++) {
            for (let c = range.s.c; c <= range.e.c; c++) {
                const cellRef = XLSX.utils.encode_cell({ r, c });
                const cell = worksheet[cellRef];
                if (!cell) continue;

                cell.s = {
                    font: {
                        bold: r === 0, // ✅ ONLY header row bold
                        name: "Calibri",
                        sz: 15,
                    },
                    alignment: {
                        vertical: "center",
                        horizontal: "left",
                        wrapText: true,
                    },
                    border: {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    },
                };
            }
        }

        worksheet["!cols"] = headers.map((h) => {
            if (h.toLowerCase() === "id") return { wch: 6 };
            if (h.toLowerCase() === "video") return { wch: 80 };
            return { wch: 30 };
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, worksheet, "Data");

        XLSX.writeFile(wb, `${pageName}  - (DEV) Ancile Hiring Tools.xlsx`);
    };



    const handlePrint = () => {
        const headers = getVisibleColumns().map((c) => `<th>${c.Header}</th>`).join("");
        const rows = getTableData()
            .map(
                (r) =>
                    `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`
            )
            .join("");

        const win = window.open("", "", "width=900,height=700");
        win.document.write(`
        <html>
          <head>
            <title>Print</title>
            <style>
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            </style>
          </head>
          <body>
            <table>
              <thead><tr>${headers}</tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </body>
        </html>
      `);
        win.document.close();
        win.print();
    };
    const handlePDF = () => {
        const doc = new jsPDF("l", "pt", "a4"); // landscape like screenshot

        const columns = getVisibleColumns().map(col => col.Header);
        const rows = getTableData();

        autoTable(doc, {
            head: [columns],
            body: rows,

            startY: 40,
            theme: "striped",

            styles: {
                fontSize: 9,
                cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
                textColor: "#000",
                lineColor: "#e5e7eb",
                lineWidth: 0.5,
                valign: "middle",
                halign: "left",
            },

            headStyles: {
                fillColor: "#2f3e4e",   // dark blue header
                textColor: "#ffffff",
                fontStyle: "bold",
                fontSize: 10,
                halign: "left",
                valign: "middle",
            },

            alternateRowStyles: {
                fillColor: "#f3f4f6", // light gray stripe
            },

            bodyStyles: {
                fillColor: "#ffffff",
            },

            didParseCell: function (data) {
                // Prevent long text overflow like emails
                data.cell.styles.cellWidth = "wrap";
            },

            margin: { left: 20, right: 20 },
        });

        doc.save(`${pageName}  - (DEV) Ancile Hiring Tools.pdf`);
    };

    const handleLinkDownload = async (url) => {
        if (!url) return;
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = "";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };




    return (
        <Card className="shadow-sm border-0">
            <CardBody className="p-0">
                {/* Toolbar */}
                <div className="d-flex align-items-center justify-content-between px-2 py-2 pad-btm candidate-toolbar-bg">
                    <div className="d-flex align-items-center gap-2">
                        <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline table-action-icon" onClick={toggleColumnModal}>
                            <i className="fa fa-columns" aria-hidden="true"></i>
                            COLUMNS
                        </Button>
                        {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline" onClick={()=>setCommonFilterModalOpen(true)}>
            <i className="fa fa-filter"></i> Filters
            </Button> */}
                        {/* <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconFilter} Filters
            </Button>
            
            <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconList} Density
            </Button> */}
                        {/* <Button color="link" className="text-dark d-flex align-items-center gap-2 p-0 fw-semibold">
              {IconDownload} Export
            </Button> */}
                        {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCopy}>
                            <i className="fa fa-copy" aria-hidden="true"></i>
                            COPY
                        </Button>
                        <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCSV}>
                            <i className="fas fa-file-csv"></i>
                            CSV
                        </Button> */}
                        <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-excel" onClick={handleExcel}>
                            <i className="fas fa-file-excel"></i>
                            EXCEL
                        </Button>
                        {/* <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-print" onClick={handlePrint}>
                            <i className="fa fa-print" aria-hidden="true"></i>
                            PRINT
                        </Button> */}
                        <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-pdf" onClick={handlePDF}>
                            <i className="fa fa-file-pdf" aria-hidden="true"></i>
                            PDF
                        </Button>
                    </div>
                    {allCommonFilter ? (
                        <TableFilterToolbarActions
                            activeFilterCount={activeFilterCount}
                            onOpenFilters={() => setCommonFilterModalOpen(true)}
                            onResetFilters={() => setCommonResetFilter(true)}
                        />
                    ) : null}

                    {/* <Button color="primary" size="sm" onClick={toggleColumnModal}>
            Manage Columns
          </Button> */}
                </div>
                {enableBulkSelection && isCheckboxLeftSide && (
                    <div className="d-flex justify-content-end align-items-center px-2 py-2 border-bottom candidate-bulk-bar-bg gap-2">
                        {enableBulkTransfer ? (
                            <Button
                                color="primary"
                                size="sm"
                                className="border-radius-zero"
                                type="button"
                                disabled={!selectedIds.size}
                                onClick={handleBulkTransferClick}
                            >
                                Bulk Candidate Transfer
                            </Button>
                        ) : null}
                        {enableMoveToAiRecruiterSchedule ? (
                            <Button
                                color="primary"
                                size="sm"
                                className="border-radius-zero"
                                type="button"
                                disabled={!selectedIds.size || moveToAiRecruiterScheduleLoading}
                                onClick={handleMoveToAiRecruiterScheduleClick}
                            >
                                {moveToAiRecruiterScheduleLoading ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin me-1" aria-hidden />
                                        Moving...
                                    </>
                                ) : (
                                    "Move to AI Recruiter Schedule"
                                )}
                            </Button>
                        ) : null}
                        {enableBulkDeactivate ? (
                            <Button
                                color="danger"
                                size="sm"
                                className="border-radius-zero mt-0"
                                type="button"
                                disabled={!selectedIds.size}
                                onClick={handleBulkDeactivateClick}
                            >
                                Deactivate Selected Candidates
                            </Button>
                        ) : null}
                    </div>
                )}
                {/* <hr /> */}



                {/* Table */}
                <div
                    className={[
                        "candidate-table-scroll-shell",
                        showFixedFilterPrompt ? "candidate-table-scroll-shell--filter-prompt" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                <div
                    ref={tableScrollRef}
                    className={[
                        "table-responsive mb-4 candidate-table-wrap-bg",
                        isScrolledToEnd ? "table-scroll-at-end" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                    <Table hover borderless className="align-middle mb-0 custom-table">
                        <DragDropContext onDragEnd={handleColumnDragEnd}>
                            <Droppable droppableId="columns" direction="horizontal">
                                {(provided) => (
                                    <thead ref={provided.innerRef} {...provided.droppableProps} className="candidate-thead-bg">
                                        <tr className="candidate-thead-row">
                                            {isCheckboxLeftSide &&

                                                <th className="table-w-40">
                                                    {enableBulkSelection ? (
                                                        <input
                                                            key={`bulk-hdr-${controlledCurrentPage}-${controlledRowsPerPage}-${allPageSelected}-${somePageSelected}`}
                                                            ref={headerCheckboxRef}
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            defaultChecked={allPageSelected}
                                                            onChange={toggleSelectAllOnPage}
                                                            aria-label="Select all on this page"
                                                        />
                                                    ) : null}
                                                </th>
                                            }

                                            {visibleColumnOrder?.map((columnPreferenceKey, index) => {
                                                const col = findTableColumnDefinitionByPreferenceKey(
                                                    columns,
                                                    columnPreferenceKey
                                                );
                                                if (!col?.accessor) return null;
                                                const draggableId =
                                                    getTableColumnPreferenceKey(col) ||
                                                    columnPreferenceKey;

                                                return (
                                                    <Draggable
                                                        key={draggableId}
                                                        draggableId={draggableId}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <th
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                onClick={() => handleSort(col?.accessor)}
                                                                className={getCandidateTableColumnHeaderClassName(
                                                                    col?.accessor,
                                                                    {
                                                                        wideDateAccessors,
                                                                        apiKeyAccessors,
                                                                        phoneAccessors,
                                                                    },
                                                                    pageName,
                                                                    col?.type
                                                                )}
                                                                style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                            >
                                                                {col?.Header} <SortIcon column={col?.accessor} />
                                                            </th>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}

                                            {provided.placeholder}
                                            {showRtrAcknowledgeCallColumn && (
                                                <th className="align-middle text-center text-nowrap">
                                                    Call
                                                </th>
                                            )}
                                            {showRtrAcknowledgeTimerColumn && (
                                                <th className="align-middle text-center text-nowrap">
                                                    Timer
                                                </th>
                                            )}
                                            {isActionRequired && (
                                                <th
                                                    className={[
                                                        "candidate-col-width-actions team-table-actions-cell align-middle text-center text-nowrap",
                                                        isCompactCandidateActionColumn
                                                            ? "candidate-col-width-actions--compact"
                                                            : "",
                                                        isRtrAcknowledgePage
                                                            ? "candidate-col-width-actions--rtr-acknowledge"
                                                            : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                >
                                                    Action
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                )}
                            </Droppable>

                            <tbody className="candidate-tbody-bg">
                                {!paginatedData?.length ? (
                                    <tr className={showFixedFilterPrompt ? "candidate-no-data-spacer-row" : ""}>
                                        <td
                                            colSpan={Math.max(noDataColSpan, 1)}
                                            className={[
                                                showFixedFilterPrompt
                                                    ? "candidate-no-data-spacer-cell"
                                                    : "candidate-no-data-cell text-center text-muted",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        >
                                            {showFixedFilterPrompt ? null : (
                                            <div className="candidate-no-data-box">
                                                {emptyTableContent ?? "No data found"}
                                            </div>
                                            )}
                                        </td>
                                    </tr>
                                ) : paginatedData?.map((item, idx) => {
                                    const rowId = getCandidateRowId(item);
                                    const isRowSelected = selectedIds.has(rowId);
                                    const detailsHref =
                                        typeof getDetailsHref === "function"
                                            ? String(getDetailsHref(item) || "").trim()
                                            : "";
                                    const rowClassName =
                                        typeof getRowClassName === "function"
                                            ? getRowClassName(item)
                                            : "";
                                    return (
                                    <tr
                                        key={rowId || `row-${idx}`}
                                        className={rowClassName || undefined}
                                    >
                                        {isCheckboxLeftSide &&
                                            <td>
                                                {enableBulkSelection ? (
                                                    <input
                                                        key={`bulk-row-${rowId}-${isRowSelected}`}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        defaultChecked={isRowSelected}
                                                        onChange={() => toggleRowId(rowId)}
                                                        disabled={!rowId}
                                                        aria-label="Select row"
                                                    />
                                                ) : (
                                                    <input type="checkbox" className="form-check-input" disabled />
                                                )}
                                            </td>
                                        }
                                        {visibleColumnOrder?.map((columnPreferenceKey) => {
                                            const col = findTableColumnDefinitionByPreferenceKey(
                                                columns,
                                                columnPreferenceKey
                                            );
                                            if (!col?.accessor) return null;
                                            const cellValue = resolveTableCellValue(item, col?.accessor);
                                            const hireColKind = isCandidateHireStatusPage
                                                ? hireStatusInlineKind(col?.accessor)
                                                : null;

                                            return (
                                                <td
                                                    key={col?.accessor}
                                                    className={[
                                                        getCandidateTableColumnCellClassName(
                                                            col?.accessor,
                                                            pageName,
                                                            col?.type
                                                        ),
                                                        getCandidateColumnWidthClass(col?.accessor, {
                                                            wideDateAccessors,
                                                            apiKeyAccessors,
                                                            phoneAccessors,
                                                        }, pageName),
                                                        hireColKind
                                                            ? "candidate-col-width-hire-inline"
                                                            : "",
                                                        hireColKind === "comments"
                                                            ? "candidate-col-width-hire-inline--comments"
                                                            : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                >
                                                    {(() => {
                                                        const hireKind = hireColKind;
                                                        const hireDraft = hireKind ? mergeHireDraft(item) : null;
                                                        const hireRowId = String(
                                                            item?.id ?? item?._id ?? ""
                                                        ).trim();
                                                        const hireRowSaving = hireStatusSavingId === hireRowId;
                                                        if (isIdCandidateTableColumnAccessor(col?.accessor)) {
                                                            return (
                                                                <MaskedIdTableCell
                                                                    maskedId={cellValue}
                                                                    className={mergeCandidateTableDataCellClasses(
                                                                        col?.accessor
                                                                    )}
                                                                />
                                                            );
                                                        }
                                                        if (isBooleanLikeMetadataTableValue(cellValue)) {
                                                            const booleanYesNoBadge =
                                                                renderMetadataTableBooleanYesNoBadge(cellValue);
                                                            if (booleanYesNoBadge) return booleanYesNoBadge;
                                                        }
                                                        if (hireKind === "dateHired") {
                                                            return (
                                                                <div className="candidate-hire-status-field candidate-hire-status-field--date">
                                                                    <Input
                                                                        type="date"
                                                                        bsSize="sm"
                                                                        className="candidate-hire-status-inline-input"
                                                                        disabled={hireRowSaving}
                                                                        value={hireDraft.dateHired || ""}
                                                                        onChange={(e) =>
                                                                            void persistHireStatusAfterChange(
                                                                                item,
                                                                                {
                                                                                    dateHired: e.target.value,
                                                                                }
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            );
                                                        }
                                                        if (hireKind === "incentive") {
                                                            return (
                                                                <div className="candidate-hire-status-field candidate-hire-status-field--incentive">
                                                                    <Input
                                                                        type="select"
                                                                        bsSize="sm"
                                                                        className="candidate-hire-status-inline-input"
                                                                        disabled={hireRowSaving}
                                                                        value={hireDraft.incentiveStatus || ""}
                                                                        onChange={(e) =>
                                                                            void persistHireStatusAfterChange(
                                                                                item,
                                                                                {
                                                                                    incentiveStatus: e.target.value,
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">
                                                                            Select incentive type
                                                                        </option>
                                                                        {hireIncentiveTypeOptions.map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </div>
                                                            );
                                                        }
                                                        if (hireKind === "clientPaid") {
                                                            return (
                                                                <div className="candidate-hire-status-field candidate-hire-status-field--client-paid">
                                                                    <Input
                                                                        type="select"
                                                                        bsSize="sm"
                                                                        className="candidate-hire-status-inline-input"
                                                                        disabled={hireRowSaving}
                                                                        value={hireDraft.clientPaid || ""}
                                                                        onChange={(e) =>
                                                                            void persistHireStatusAfterChange(
                                                                                item,
                                                                                {
                                                                                    clientPaid: e.target.value,
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">
                                                                            Select client paid
                                                                        </option>
                                                                        {HIRE_CLIENT_PAID_OPTIONS.map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </div>
                                                            );
                                                        }
                                                        if (hireKind === "status") {
                                                            return (
                                                                <div className="candidate-hire-status-field candidate-hire-status-field--status">
                                                                    <Input
                                                                        type="select"
                                                                        bsSize="sm"
                                                                        className="candidate-hire-status-inline-input"
                                                                        disabled={hireRowSaving}
                                                                        value={hireDraft.status || ""}
                                                                        onChange={(e) =>
                                                                            void persistHireStatusAfterChange(
                                                                                item,
                                                                                {
                                                                                    status: e.target.value,
                                                                                }
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">Select status</option>
                                                                        {hireOrderStatusOptions.map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </div>
                                                            );
                                                        }
                                                        if (hireKind === "comments") {
                                                            return (
                                                                <div className="candidate-hire-status-field candidate-hire-status-field--comments">
                                                                    <Input
                                                                        type="textarea"
                                                                        rows={8}
                                                                        bsSize="sm"
                                                                        className="candidate-hire-status-inline-input candidate-hire-status-inline-textarea"
                                                                        placeholder="comment"
                                                                        disabled={hireRowSaving}
                                                                        value={hireDraft.comments ?? ""}
                                                                        onChange={(e) =>
                                                                            setHireDraftLocal(item, {
                                                                                comments: e.target.value,
                                                                            })
                                                                        }
                                                                        onBlur={() =>
                                                                            void persistHireStatusAfterChange(
                                                                                item,
                                                                                {}
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        return col?.accessor === "incentive" ? (
                                                        <span className="px-2 py-1 fw-semibold accessor-incentive">
                                                            {cellValue}
                                                        </span>
                                                    )
                                                        // : col?.accessor === "quota" ? (
                                                        //   <div className="text-center">
                                                        //     {item[col?.accessor] ? (
                                                        //       <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "19px", height: "19px", borderRadius: "50%", backgroundColor: "#22c55e" }}>
                                                        //         <svg width="14" height="14" fill="#fff">
                                                        //           <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                                        //         </svg>
                                                        //       </div>
                                                        //     ) : (
                                                        //       <span className="text-muted">—</span>
                                                        //     )}
                                                        //   </div>
                                                        // ) 
                                                        // :
                                                        //   (col?.accessor === 'link') ? (
                                                        //     <span className={`px-2 py-1 fw-semibold`} >
                                                        //       <Link target="_blank" href={`${process.env.REACT_APP_LINK_URL}${col?.accessor}`}></Link>
                                                        //       {item[col?.accessor]}
                                                        //     </span>
                                                        //   ) 
                                                        :
                                                        isCandidateFunnlStatusColumn(col?.accessor) ? (
                                                            (() => {
                                                                const funnlStatusDisplayValue =
                                                                    normalizeCandidateStatusMetadataValue(
                                                                        col?.accessor,
                                                                        cellValue
                                                                    );
                                                                if (
                                                                    funnlStatusDisplayValue === null ||
                                                                    funnlStatusDisplayValue === undefined ||
                                                                    String(funnlStatusDisplayValue).trim() === ""
                                                                ) {
                                                                    return <span className="text-muted">—</span>;
                                                                }
                                                                return (
                                                                    <span className={TABLE_STATUS_BADGE_CLASS}>
                                                                        {String(funnlStatusDisplayValue)}
                                                                    </span>
                                                                );
                                                            })()
                                                        )
                                                        :
                                                        isCandidateTableStatusMetadataColumn(col?.accessor, col?.type) ? (
                                                            <StatusMetadataBadge
                                                                accessor={col?.accessor}
                                                                pageName={pageName}
                                                                value={normalizeCandidateStatusMetadataValue(
                                                                    col?.accessor,
                                                                    cellValue
                                                                )}
                                                                fallbackClassName={statusType(
                                                                    String(cellValue ?? "")
                                                                )}
                                                            />
                                                        )
                                                        :
                                                        isCandidateTableYesNoColumn(col?.accessor, col?.type) ? (
                                                            (() => {
                                                                const yesNoBadge =
                                                                    renderCandidateTableYesNoQuotaBadge(cellValue);
                                                                if (yesNoBadge) return yesNoBadge;
                                                                if (
                                                                    cellValue === null ||
                                                                    cellValue === undefined ||
                                                                    String(cellValue).trim() === ""
                                                                ) {
                                                                    return <span className="text-muted">—</span>;
                                                                }
                                                                return (
                                                                    <span
                                                                        className={mergeCandidateTableDataCellClasses(
                                                                            col?.accessor
                                                                        )}
                                                                    >
                                                                        {String(cellValue)}
                                                                    </span>
                                                                );
                                                            })()
                                                        )
                                                        :
                                                        (["isUsCitizen"].includes(col?.accessor)) ? (
                                                            <span className={`px-2 py-1 fw-semibold`} >
                                                                {citizenType(String(cellValue))}
                                                            </span>
                                                        )
                                                        :
                                                        (col?.accessor === 'dataProcessed') ? (
                                                            <span className={`${TABLE_STATUS_BADGE_CLASS} ${statusTypeTrueFalse(String(cellValue))}`} >
                                                                {cellValue ? 'True' : 'False'}
                                                            </span>
                                                        )
                                                            :
                                                            (["type", "adSpend", "plan", "subscribed"].includes(col?.accessor)) ? (
                                                                <span className={`${TABLE_STATUS_BADGE_CLASS} ${cellValue ? onBoardingStatusType(cellValue) : ""}`} >
                                                                    {cellValue}
                                                                </span>
                                                            )
                                                                : (col?.accessor === "incentive") ? (
                                                                            <span className={`px-2 py-1 fw-semibold ${incentiveType(cellValue)}`}>
                                                                                {cellValue}
                                                                            </span>
                                                                        ) : (["link", "file", "downloadURL", "CV", "resumeUploadLink"].includes(col?.accessor)) ? (
                                                                            cellValue ? (
                                                                                <Button
                                                                                    color="link"
                                                                                    className="p-0 text-decoration-none"
                                                                                    onClick={() => handleLinkDownload(cellValue)}
                                                                                    title="Download"
                                                                                >
                                                                                    <i className="fa fa-download" aria-hidden="true"></i>
                                                                                </Button>
                                                                            ) : (
                                                                                <span>-</span>
                                                                            )
                                                                        ) : col?.accessor === "notes" ? (
                                                                            isRtrAcknowledgePage &&
                                                                            typeof onRowNotesChange !== "function" ? (
                                                                                <textarea
                                                                                    className="form-control textarea-width"
                                                                                    rows={2}
                                                                                    value={cellValue ?? ""}
                                                                                    disabled
                                                                                    readOnly
                                                                                />
                                                                            ) : typeof onRowNotesChange === "function" ? (
                                                                                <textarea
                                                                                    className="form-control textarea-width"
                                                                                    rows={2}
                                                                                    value={cellValue ?? ""}
                                                                                    onChange={(event) =>
                                                                                        onRowNotesChange(item, event.target.value)
                                                                                    }
                                                                                    onBlur={
                                                                                        typeof onRowNotesBlur === "function"
                                                                                            ? (event) =>
                                                                                                onRowNotesBlur(
                                                                                                    item,
                                                                                                    event.target.value
                                                                                                )
                                                                                            : undefined
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <textarea
                                                                                    className="form-control textarea-width"
                                                                                    rows={2}
                                                                                    defaultValue={cellValue}
                                                                                    onBlur={
                                                                                        typeof onRowNotesBlur === "function"
                                                                                            ? (event) =>
                                                                                                onRowNotesBlur(
                                                                                                    item,
                                                                                                    event.target.value
                                                                                                )
                                                                                            : undefined
                                                                                    }
                                                                                />
                                                                            )
                                                                        )
                                                                            : col?.accessor === "fedex_id" ? (
                                                                                <textarea className="form-control textarea-width" rows={1} defaultValue={cellValue} />
                                                                            ) :
                                                                                (["apikey", "api_key"].includes(String(col?.accessor || "").toLowerCase())) ? (
                                                                                    (() => {
                                                                                        const rowKey = item?.id || item?._id || idx;
                                                                                        const visibilityKey = `${rowKey}-${col?.accessor}`;
                                                                                        const isVisible = !!visibleApiKeyMap?.[visibilityKey];
                                                                                        const hasValue = !!String(cellValue || "").trim();
                                                                                        return (
                                                                                            <div className="d-flex align-items-center gap-2">
                                                                                                <span>{hasValue ? (isVisible ? cellValue : "XXXXXXXXXXXXXXXXXXXX") : ""}</span>
                                                                                                {hasValue ? (
                                                                                                    <span
                                                                                                        role="button"
                                                                                                        className="text-muted cursor-pointer candidate-api-eye-offset"
                                                                                                        onClick={() => toggleApiKeyVisibility(rowKey, col?.accessor)}
                                                                                                        title={isVisible ? "Hide API key" : "Show API key"}
                                                                                                    >
                                                                                                        <i
                                                                                                            className={`mdi ms-1 ${isVisible ? "mdi-eye-off-outline" : "mdi-eye-outline"} font-size-18`}
                                                                                                        ></i>
                                                                                                    </span>
                                                                                                ) : null}
                                                                                            </div>
                                                                                        );
                                                                                    })()
                                                                                ) :
                                                                                    ["candidateAndSource", "clientAndRecruiter"].includes(col?.accessor) ? (
                                                                                        <span
                                                                                            className={[
                                                                                                mergeCandidateTableDataCellClasses(col?.accessor),
                                                                                                "common-candidate-table-html-dangerous",
                                                                                            ]
                                                                                                .filter(Boolean)
                                                                                                .join(" ")}
                                                                                            dangerouslySetInnerHTML={{
                                                                                                __html: String(cellValue ?? ""),
                                                                                            }}
                                                                                        />
                                                                                    ) :
                                                                                    col?.accessor === "is_allowed" ? (
                                                                                        <input type="checkbox" className="form-check-input" onChange={(e) => onToggleAllowed(item, e.target.checked)} />
                                                                                    )
                                                                                        : isPhoneColumnAccessor(col?.accessor) ? (
                                                                                            (() => {
                                                                                                const formattedPhone =
                                                                                                    phoneNumberShowFormat(cellValue);
                                                                                                if (!formattedPhone) {
                                                                                                    return <span className="text-muted">—</span>;
                                                                                                }
                                                                                                return (
                                                                                                    <span
                                                                                                        className={mergeCandidateTableDataCellClasses(
                                                                                                            col?.accessor
                                                                                                        )}
                                                                                                    >
                                                                                                        {formattedPhone}
                                                                                                    </span>
                                                                                                );
                                                                                            })()
                                                                                        )
                                                                                        : (
                                                                                            <span className={mergeCandidateTableDataCellClasses(col?.accessor)}>
                                                                                                {isRtrAcknowledgePage
                                                                                                    ? renderRtrAcknowledgeMetadataCellValue(
                                                                                                          cellValue,
                                                                                                          col
                                                                                                      )
                                                                                                    : formatMetadataTableCellDateTime(
                                                                                                          cellValue,
                                                                                                          col?.type,
                                                                                                          col?.accessor
                                                                                                      )}
                                                                                            </span>
                                                                                        )
                                                    ;
                                                    })()}
                                                </td>
                                            );
                                        })}
                                        {showRtrAcknowledgeCallColumn && (
                                            <td className="align-middle text-center px-1 py-1">
                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                    <span
                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => onRtrAcknowledgeCall(item)}
                                                        id={`rtr-ack-call-${item?.id || item?._id || item?.candidateClientId || idx}`}
                                                        role="button"
                                                        aria-label="Call"
                                                    >
                                                        <i className="mdi mdi-phone text-success" aria-hidden />
                                                    </span>
                                                    <UncontrolledTooltip
                                                        target={`rtr-ack-call-${item?.id || item?._id || item?.candidateClientId || idx}`}
                                                        placement="top"
                                                    >
                                                        Call
                                                    </UncontrolledTooltip>
                                                </div>
                                            </td>
                                        )}
                                        {showRtrAcknowledgeTimerColumn && (
                                            <td className="align-middle text-center px-1 py-1">
                                                <RtrAcknowledgeElapsedTimer
                                                    createdDate={resolveRtrAcknowledgeCreatedDate(item)}
                                                    frozen={isRtrAcknowledgeAcknowledged(item)}
                                                />
                                            </td>
                                        )}
                                        {isActionRequired &&
                                            <td
                                                className={[
                                                    "align-middle text-center team-table-actions-cell px-1 py-1",
                                                    isCompactCandidateActionColumn
                                                        ? "team-table-actions-cell--compact"
                                                        : "candidate-col-width-actions",
                                                    isRtrAcknowledgePage
                                                        ? "candidate-col-width-actions--rtr-acknowledge"
                                                        : "",
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            >
                                                {/* {isActionRequired && ( */}
                                                <>
                                                {String(pageName || "").toLowerCase() === "candidates" && (
                                                            <div className="d-inline-flex flex-wrap align-items-center justify-content-center candidate-action-grid">
                                                                {typeof onEdit === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onEdit(item)}
                                                                        id={`candidate-edit-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-pencil text-success" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidate-edit-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Edit
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}
                                                                {(typeof onDetails === "function" || detailsHref) && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    {detailsHref ? (
                                                                        <Link
                                                                            to={detailsHref}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                            id={`candidates-call-details-${item?.id || item?._id || idx}`}
                                                                            aria-label="Call details"
                                                                        >
                                                                            <i className="mdi mdi-phone-log text-success" aria-hidden />
                                                                        </Link>
                                                                    ) : (
                                                                        <span
                                                                            className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                            onClick={() => onDetails(item)}
                                                                            id={`candidates-call-details-${item?.id || item?._id || idx}`}
                                                                            role="button"
                                                                        >
                                                                            <i className="mdi mdi-phone-log text-success" aria-hidden />
                                                                        </span>
                                                                    )}
                                                                    <UncontrolledTooltip
                                                                        target={`candidates-call-details-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Call details
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onDriverDetails === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onDriverDetails(item)}
                                                                        id={`candidates-driver-details-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-card-account-details-outline text-primary" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidates-driver-details-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Driver details
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onTodayCall === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onTodayCall(item)}
                                                                        id={`candidates-today-call-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-calendar-today text-primary" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidates-today-call-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Sure to add today's call?
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onSureMailSend === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onSureMailSend(item)}
                                                                        id={`candidates-sure-mail-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-email-send-outline text-secondary" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidates-sure-mail-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Sure to reset mail send?
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onResetCandidate === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onResetCandidate(item)}
                                                                        id={`candidate-reset-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-backup-restore text-danger" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidate-reset-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Reset candidate
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onAddClosure === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onAddClosure(item)}
                                                                        id={`candidate-closure-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-clipboard-check text-warning" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidate-closure-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Add To Closure
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onMarkAsHired === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onMarkAsHired(item)}
                                                                        id={`candidate-mark-hired-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-account-check text-success" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidate-mark-hired-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Mark as hired
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}

                                                                {typeof onMarkAsRejected === "function" && (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onMarkAsRejected(item)}
                                                                        id={`candidate-mark-rejected-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-close-circle text-danger" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`candidate-mark-rejected-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Mark as rejected
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {String(pageName || "").toLowerCase() === "candidate-hire-status" && (
                                                            <div className="d-inline-flex align-items-center justify-content-center">
                                                                <span
                                                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        const open = onHireStatusEdit ?? onEdit;
                                                                        if (typeof open === "function") {
                                                                            open(item);
                                                                        }
                                                                    }}
                                                                    id={`hire-status-edit-${item?.id || item?._id || idx}`}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    aria-label="Edit hire status"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.preventDefault();
                                                                            const open = onHireStatusEdit ?? onEdit;
                                                                            if (typeof open === "function") open(item);
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-pencil text-success" aria-hidden />
                                                                </span>
                                                                <UncontrolledTooltip target={`hire-status-edit-${item?.id || item?._id || idx}`}>
                                                                    Edit
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        )}
                                                        {String(pageName || "").toLowerCase() === "call-back-request" && (
                                                            <div
                                                                className="d-inline-grid align-items-center justify-items-center"
                                                            >
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onEdit?.(item)}
                                                                        id={`call-back-edit-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-pencil text-success" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`call-back-edit-${item?.id || item?._id || idx}`}
                                                                    >
                                                                        Edit
                                                                    </UncontrolledTooltip>
                                                                </div>

                                                                <div aria-hidden className="d-inline-flex" />
                                                            </div>
                                                        )}
                                                        {String(pageName || "").toLowerCase() === "fadv-csv-log" && (
                                                            <div className="d-inline-flex align-items-center justify-content-center">
                                                                <span
                                                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                    onClick={() => onViewImage?.(item)}
                                                                    id={`fadv-csv-image-${item?.id || item?._id || idx}`}
                                                                    role="button"
                                                                >
                                                                    <i className="mdi mdi-file-image-outline text-info" aria-hidden />
                                                                </span>
                                                                <UncontrolledTooltip
                                                                    target={`fadv-csv-image-${item?.id || item?._id || idx}`}
                                                                >
                                                                    View image
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        )}
                                                        {(isFadvOrderLogPage || isFadvProfileOrderLogPage) && (
                                                            <div className="d-inline-flex align-items-center justify-content-center">
                                                                <span
                                                                    className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                    onClick={() => onTriggerFadv?.(item)}
                                                                    id={`fadv-order-send-${item?.id || item?._id || idx}`}
                                                                    role="button"
                                                                >
                                                                    <i className="mdi mdi-send text-primary" aria-hidden />
                                                                </span>
                                                                <UncontrolledTooltip
                                                                    target={`fadv-order-send-${item?.id || item?._id || idx}`}
                                                                >
                                                                    {isFadvProfileOrderLogPage
                                                                        ? "Trigger FADV profile API"
                                                                        : "Trigger FADV API"}
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        )}
                                                    {String(pageName || "").toLowerCase() === "verifytest" && (
                                                        <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                                            <span
                                                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                onClick={() => onVerifyTestApprove?.(item)}
                                                                id={`verify-test-approve-${item?.id || item?._id || idx}`}
                                                                role="button"
                                                            >
                                                                <i className="mdi mdi-check-circle text-success" aria-hidden />
                                                            </span>
                                                            <UncontrolledTooltip target={`verify-test-approve-${item?.id || item?._id || idx}`}>
                                                                Approve
                                                            </UncontrolledTooltip>
                                                            <span
                                                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                onClick={() => onVerifyTestCancel?.(item)}
                                                                id={`verify-test-cancel-${item?.id || item?._id || idx}`}
                                                                role="button"
                                                            >
                                                                <i className="mdi mdi-close-circle text-danger" aria-hidden />
                                                            </span>
                                                            <UncontrolledTooltip target={`verify-test-cancel-${item?.id || item?._id || idx}`}>
                                                                Cancel
                                                            </UncontrolledTooltip>
                                                        </div>
                                                    )}
                                                    {isBadFitResolutionPage && (
                                                        (() => {
                                                            if (!isBadFitTrackingUnresolved(item)) {
                                                                return <span className="text-muted">—</span>;
                                                            }
                                                            if (typeof onBadFitConvertToGoodFit !== "function") {
                                                                return <span className="text-muted">—</span>;
                                                            }
                                                            const rowActionId = getBadFitTrackingIdFromRow(item) || idx;
                                                            return (
                                                                <div className="d-inline-flex align-items-center justify-content-center">
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onBadFitConvertToGoodFit(item)}
                                                                        id={`bad-fit-convert-${rowActionId}`}
                                                                        role="button"
                                                                        aria-label="Convert To Good Fit"
                                                                    >
                                                                        <i className="mdi mdi-account-alert-outline text-success" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`bad-fit-convert-${rowActionId}`}
                                                                        placement="top"
                                                                    >
                                                                        Convert To Good Fit
                                                                    </UncontrolledTooltip>
                                                                </div>
                                                            );
                                                        })()
                                                    )}
                                                    {String(pageName || "").toLowerCase() === "rtracknowledge" && (
                                                        (() => {
                                                            const canShowAcknowledgeButtons =
                                                                isRtrAcknowledgePending(item) &&
                                                                (typeof onRtrAcknowledgeYes === "function" ||
                                                                    typeof onRtrAcknowledgeNo === "function");
                                                            const canShowDeleteButton =
                                                                typeof onDelete === "function";
                                                            if (!canShowAcknowledgeButtons && !canShowDeleteButton) {
                                                                return <span className="text-muted">—</span>;
                                                            }
                                                            return (
                                                            <div className="d-inline-flex align-items-center justify-content-center gap-2 flex-nowrap rtr-acknowledge-action-btns">
                                                                {canShowAcknowledgeButtons ? (
                                                                    <>
                                                                        {typeof onRtrAcknowledgeYes === "function" ? (
                                                                            <span
                                                                                className="team-table-row-action-btn team-table-row-action-btn--labeled rtr-acknowledge-action-btn--yes d-inline-flex align-items-center justify-content-center gap-1"
                                                                                onClick={() => onRtrAcknowledgeYes(item)}
                                                                                id={`rtr-ack-yes-${item?.id || item?._id || idx}`}
                                                                                role="button"
                                                                                aria-label="Yes"
                                                                            >
                                                                                <i className="mdi mdi-check-circle text-success" aria-hidden />
                                                                                <span className="team-table-row-action-btn__label">Yes</span>
                                                                            </span>
                                                                        ) : null}
                                                                        {typeof onRtrAcknowledgeNo === "function" ? (
                                                                            <span
                                                                                className="team-table-row-action-btn team-table-row-action-btn--labeled rtr-acknowledge-action-btn--no d-inline-flex align-items-center justify-content-center gap-1"
                                                                                onClick={() => onRtrAcknowledgeNo(item)}
                                                                                id={`rtr-ack-no-${item?.id || item?._id || idx}`}
                                                                                role="button"
                                                                                aria-label="No"
                                                                            >
                                                                                <i className="mdi mdi-close-circle text-danger" aria-hidden />
                                                                                <span className="team-table-row-action-btn__label">No</span>
                                                                            </span>
                                                                        ) : null}
                                                                    </>
                                                                ) : null}
                                                                {canShowDeleteButton ? (
                                                                    <span
                                                                        className="team-table-row-action-btn team-table-row-action-btn--labeled rtr-acknowledge-action-btn--delete d-inline-flex align-items-center justify-content-center gap-1"
                                                                        onClick={() => onDelete(item)}
                                                                        id={`rtr-ack-delete-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                        aria-label="Delete"
                                                                    >
                                                                        <i className="mdi mdi-delete-circle" aria-hidden />
                                                                        <span className="team-table-row-action-btn__label">Delete</span>
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            );
                                                        })()
                                                    )}
                                                    {String(pageName || "").toLowerCase() === "callaudit" && (
                                                        <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                                            {typeof onEdit === "function" &&
                                                                (item?.canEdit === undefined || item?.canEdit === true) && (
                                                                <>
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onEdit(item)}
                                                                        id={`call-audit-edit-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-pencil text-success" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip target={`call-audit-edit-${item?.id || item?._id || idx}`}>
                                                                        Edit
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            )}
                                                            {typeof onView === "function" && (
                                                                <>
                                                                    <span
                                                                        className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                        onClick={() => onView(item)}
                                                                        id={`call-audit-view-${item?.id || item?._id || idx}`}
                                                                        role="button"
                                                                    >
                                                                        <i className="mdi mdi-eye text-info" aria-hidden />
                                                                    </span>
                                                                    <UncontrolledTooltip target={`call-audit-view-${item?.id || item?._id || idx}`}>
                                                                        View
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                    {String(pageName || "").toLowerCase() === "modifyclosure" && (
                                                        <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                                                            <span
                                                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                onClick={() => onEdit?.(item)}
                                                                id={`modify-closure-edit-${item?.id || item?._id || idx}`}
                                                                role="button"
                                                            >
                                                                <i className="mdi mdi-pencil text-success" aria-hidden />
                                                            </span>
                                                            <UncontrolledTooltip target={`modify-closure-edit-${item?.id || item?._id || idx}`}>
                                                                Edit
                                                            </UncontrolledTooltip>
                                                            <span
                                                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                onClick={() => onDelete?.(item)}
                                                                id={`modify-closure-delete-${item?.id || item?._id || idx}`}
                                                                role="button"
                                                            >
                                                                <i className="mdi mdi-delete text-danger" aria-hidden />
                                                            </span>
                                                            <UncontrolledTooltip target={`modify-closure-delete-${item?.id || item?._id || idx}`}>
                                                                Delete
                                                            </UncontrolledTooltip>
                                                        </div>
                                                    )}
                                                   
                                                </>
                                                {/* //   )} */}
                                            </td>
                                        }
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </DragDropContext>
                    </Table>
                </div>
                {showFixedFilterPrompt ? (
                    <div className="candidate-table-filter-prompt-fixed">
                        {emptyTableContent}
                    </div>
                ) : null}
                </div>

                {/* Pagination */}
                {isPagination ?
                    <div className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-white">
                        <div className="d-flex align-items-center gap-2 me-3">
                            <span className="text-muted small">Rows per page:</span>
                            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                                <DropdownToggle caret color="light" size="sm" className="border bg-white">
                                    {formatPageSizeLabel(rowsPerPage)}
                                </DropdownToggle>
                                <DropdownMenu>
                                    {pageSizeOptions.map((num) => (
                                        <DropdownItem key={num} onClick={() => handleRowsPerPageChange(num)}>
                                            {formatPageSizeLabel(num)}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                        <span className="text-muted small me-3">
                            {displayFrom}–{displayTo} of {totalRecordsCount}
                        </span>
                        <div className="table-pagination-nav">
                            <Button
                                color="link"
                                className="table-pagination-arrow-btn text-muted"
                                disabled={currentPage === 1}
                                onClick={handlePrevPage}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                                </svg>
                            </Button>

                            {paginationPageItems.map((item, index) =>
                                item === PAGINATION_ELLIPSIS ? (
                                    <span
                                        key={`ellipsis-${index}`}
                                        className="table-pagination-ellipsis"
                                        aria-hidden="true"
                                    >
                                        …
                                    </span>
                                ) : (
                                    <Button
                                        key={item}
                                        color="link"
                                        className={`table-pagination-page-btn ${
                                            item === currentPage
                                                ? "is-active text-primary"
                                                : "text-muted"
                                        }`}
                                        onClick={() => handlePageNumberClick(item)}
                                        aria-current={item === currentPage ? "page" : undefined}
                                    >
                                        {item}
                                    </Button>
                                )
                            )}

                            <Button
                                color="link"
                                className="table-pagination-arrow-btn text-muted"
                                disabled={currentPage === totalPages}
                                onClick={handleNextPage}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                                </svg>
                            </Button>
                        </div>
                    </div> : null
                }

                {/* Column Modal */}
                <Modal size="md" isOpen={showColumnModal} toggle={toggleColumnModal} backdrop="static" centered={true} className="modal-full-height"
                >
                    <ModalHeader toggle={toggleColumnModal}>Manage Columns</ModalHeader>
                    <ModalBody className="p-4 modal-height-fixed">
                        <ManageColumnsModalFields
                            columns={columns}
                            columnOrder={columnOrder}
                            orderedColumns={orderedColumns}
                            visibleColumns={visibleColumns}
                            onToggle={handleColumnToggle}
                            tableKey={resolvedTableKey}
                            showColumnModal={showColumnModal}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleColumnModal}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {enableBulkTransfer ? (
                    <CandidateBulkTransferModal
                        isOpen={bulkTransferModalOpen}
                        onClose={closeBulkTransferModal}
                        candidateIds={Array.from(selectedIds)}
                        selectedCandidates={selectedCandidatesForBulkTransfer}
                        onSubmit={handleBulkTransferSubmit}
                        pageName={pageName}
                    />
                ) : null}
            </CardBody>
        </Card>
    );
};

export default CommonTableForCandidate;
